import { Badge } from '@/components/ui/badge';
import { DmsAgentRole, DmsAgentRoleLabels } from '@/constants/ai.enums';

interface AgentStep {
  id: string;
  agent_name: string;
  agent_role: DmsAgentRole | string;
  input: any;
  output: any;
  tokens_used?: number;
  duration_ms?: number;
  created_at: string;
}

export const AgentTracePanel = ({ agentStep }: { agentStep: AgentStep }) => {
  // Ensure agent_role is always treated as a string for mapping
  const roleDisplay = typeof agentStep.agent_role === 'string' && 
    Object.keys(DmsAgentRoleLabels).includes(agentStep.agent_role.toUpperCase()) 
    ? DmsAgentRoleLabels[agentStep.agent_role.toUpperCase() as DmsAgentRole]
    : agentStep.agent_role;

  // Determine badge variant based on agent role
  const roleColorMap: Record<string, 'success' | 'destructive' | 'secondary' | 'outline'> = {
    PLANNER: 'secondary',
    RETRIEVER: 'secondary',
    SUMMARIZER: 'success',
    CLASSIFIER: 'outline',
    PARSER: 'outline',
    POSTPROCESSOR: 'outline',
    OTHER: 'outline',
  };

  const badgeVariant = typeof agentStep.agent_role === 'string' && 
    Object.keys(roleColorMap).includes(agentStep.agent_role.toUpperCase())
    ? roleColorMap[agentStep.agent_role.toUpperCase()]
    : 'outline';

  return (
    <div className="p-4 mb-4 rounded bg-gray-100 dark:bg-gray-800 shadow">
      <div className="flex justify-between mb-2">
        <div>
          <span className="font-bold">{agentStep.agent_name}</span>{' '}
          <Badge variant={badgeVariant}>{roleDisplay}</Badge>
        </div>
        <div className="text-sm text-gray-500">
          {agentStep.tokens_used && `${agentStep.tokens_used} tokens`}{agentStep.tokens_used && agentStep.duration_ms && ' · '}
          {agentStep.duration_ms && `${agentStep.duration_ms}ms`}
          {!agentStep.tokens_used && !agentStep.duration_ms && 
            <span className="italic text-gray-400">No metrics</span>
          }
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2">
        Created at: {new Date(agentStep.created_at).toLocaleString()}
      </div>

      <div className="mb-2">
        <strong className="text-xs text-gray-600 dark:text-gray-400">Input:</strong>
        <pre className="whitespace-pre-wrap text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 mt-1 overflow-auto max-h-64">
          {JSON.stringify(agentStep.input, null, 2)}
        </pre>
      </div>

      <div>
        <strong className="text-xs text-gray-600 dark:text-gray-400">Output:</strong>
        <pre className="whitespace-pre-wrap text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 mt-1 overflow-auto max-h-64">
          {JSON.stringify(agentStep.output, null, 2)}
        </pre>
      </div>
    </div>
  );
};
